// @flow

import type {
  CartSummary,
  CartSummaryItem,
  CartSummaryItemPromotion,
  CartSummaryItemCoupon,
  SCTax,
} from '../../../entities';

const getUniqueSummaryPromotions = (
  items: CartSummaryItem[]
): CartSummaryItemPromotion[] => {
  const uniquePromotionsMap: Map<string, CartSummaryItemPromotion> = new Map();

  for (const item of items) {
    for (const promotion of item.promotions) {
      const existingPromotion = uniquePromotionsMap.get(promotion.id);
      if (existingPromotion) {
        existingPromotion.amount = (
          parseFloat(existingPromotion.amount) + parseFloat(promotion.amount)
        ).toString();
        existingPromotion.discount = (
          parseFloat(existingPromotion.discount) +
          parseFloat(promotion.discount)
        ).toString();
      } else {
        uniquePromotionsMap.set(promotion.id, {...promotion});
      }
    }
  }

  return Array.from(uniquePromotionsMap.values());
};

const getUniqueSummaryTaxes = (items: CartSummaryItem[]): SCTax[] => {
  const uniqueTaxesMap = new Map();

  for (const product of items) {
    for (const tax of product.taxes) {
      if (!uniqueTaxesMap.has(tax.id)) {
        uniqueTaxesMap.set(tax.id, tax);
      }
    }
  }

  return Array.from<SCTax>(uniqueTaxesMap.values());
};

const getUniqueSummaryCoupons = (
  items: CartSummaryItem[]
): CartSummaryItemCoupon[] => {
  const uniqueCouponsMap = new Map();

  for (const product of items) {
    for (const coupon of product.coupons) {
      if (!uniqueCouponsMap.has(coupon.id)) {
        uniqueCouponsMap.set(coupon.id, coupon);
      }
    }
  }

  return Array.from<CartSummaryItemCoupon>(uniqueCouponsMap.values());
};

export const getDeliveryCost = (
  shippingCost?: string,
  shippingPromotionDiscount?: string
) => {
  if (!shippingCost) return;
  const promotionDiscount = shippingPromotionDiscount
    ? Math.abs(Number(shippingPromotionDiscount))
    : 0;
  return Number(shippingCost) - promotionDiscount;
};

export const cartSummaryAdapter = (cartSummary: CartSummary) => {
  const {summary, detail, applicableCoupons = []} = cartSummary;

  const {items, coupon, globalDiscount} = detail;
  const {
    total,
    subtotal: subTotal,
    couponDiscount,
    shipping,
    shippingPromotionDiscount,
  } = summary;

  const coupons = getUniqueSummaryCoupons(items);
  const taxes = getUniqueSummaryTaxes(items);
  const promotions = getUniqueSummaryPromotions(items);
  const deliveryCost = getDeliveryCost(shipping, shippingPromotionDiscount);

  return {
    items,
    total,
    subTotal,
    coupon: coupon ?? coupons[0],
    couponDiscount,
    promotions,
    taxes,
    deliveryCost,
    globalDiscount,
    applicableCoupons,
  };
};
